import { useEffect, useRef, useState, forwardRef, useImperativeHandle } from "react";
import { useSelector } from "react-redux";
import PropTypes from "prop-types";
import * as THREE from "three";
import { OrbitControls } from "@three-ts/orbit-controls";

import fontFile from "../../assets/helvetiker_regular.typeface.json";
import {
  findHeelPoint
} from '../../common/functions';

const Cube = forwardRef(({
  canvasStyle,
  groupedStages,
  stageDepth,
  fullScreen = false,
  handleFullScreen,
  handleStageClick,
  selectedTreatmentWell,
  directionalSurveys = [],
  toggleFpzRadius = true,
  toggleOffsetWells = true,
  setCubeImageDataUrl = () => { },
  toggleRotate = true,
  ...rest
}, ref) => {
  console.log('Cube');
  console.log({ directionalSurveys });
  const containerRef = useRef(null);
  const canvasRef = useRef(null);
  const cubeRef = useRef(null);
  const cylinderRef = useRef(null);
  const textMeshRef = useRef(null);
  const azimuthRingRef = useRef(null);
  const rendererRef = useRef(null);
  const cameraRef = useRef();

  // const zipperViewData = useSelector(state => state?.chart?.zipperViewData);
  const { selectedTreatmentWells } = useSelector(state => state?.fpzReducer);

  const [selectedStageData, setSelectedStageData] = useState(null);

  const sceneRef = useRef(new THREE.Scene());

  // Create an array to hold all point meshes for easier intersection checks
  const pointMeshes = [];
  const cylinderMeshes = [];
  const ringMeshes = [];
  const textMeshes = [];
  const planes = [];

  useImperativeHandle(ref, () => ({
    captureImage: () => {
      if (rendererRef.current && sceneRef.current && cameraRef.current) {
        rendererRef.current.render(sceneRef.current, cameraRef.current);  // Force render first
        const url = rendererRef.current.domElement.toDataURL("image/png");
        setCubeImageDataUrl(url);
        return url;
      }
    }
  }));

  useEffect(() => {
    console.log('cube', { directionalSurveys, selectedTreatmentWell });
    // if (!directionalSurveys?.length || !selectedTreatmentWell) return;
    if (!directionalSurveys?.length) return;

    console.log('cube useEffect');

    const scene = sceneRef.current;

    // setSelectedStageData(zipperViewData?.selectedStageData || null);
    //if cube already exists, remove firest
    if (cubeRef.current) {
      scene.remove(cubeRef.current);
      // Dispose of geometry and material as well
      cubeRef.current.geometry.dispose();
      cubeRef.current.material.dispose();
      // Clean up existing objects
      [cylinderRef, textMeshRef, azimuthRingRef].forEach(ref => {
        if (ref.current) {
          scene.remove(ref.current);
          if (ref.current.geometry) ref.current.geometry.dispose();
          if (ref.current.material) ref.current.material.dispose();
          // Additional disposals if needed
        }
      });
    }

    const container = containerRef.current;
    const canvas = canvasRef.current;


    const camera = new THREE.PerspectiveCamera(75, container.clientWidth / container.clientHeight, 0.1, 1000);
    cameraRef.current = camera;
    const renderer = new THREE.WebGLRenderer({
      canvas,
      antialias: true,
      alpha: true
    });
    renderer.setSize(container.clientWidth, container.clientHeight);
    rendererRef.current = renderer;

    // needed for click events
    const raycaster = new THREE.Raycaster();
    const mouse = new THREE.Vector2();

    //load font to use in canvas
    let font = new THREE.Font(fontFile);

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enabled = toggleRotate;

    // Create a container cube
    const cubeSize = 500;
    const cubeGeometry = new THREE.BoxGeometry(cubeSize, cubeSize, cubeSize);
    cubeGeometry.translate(0, 0, 0); // Move the pivot point to the corner
    const cubeMaterial = new THREE.MeshBasicMaterial({ color: "#CCCCCC", wireframe: true });
    const containerCube = new THREE.Mesh(cubeGeometry, cubeMaterial);
    cubeRef.current = containerCube;
    scene.add(cubeRef.current);


    // Convert DMS to Decimal
    function dmsToDecimal(dmsString) {
      if (typeof dmsString !== 'string') {
        //not dms; assume it is decimal and make no change
        return dmsString;
      }
      const parts = dmsString.split(' ');
      const degrees = parseFloat(parts[0]);
      const minutes = parseFloat(parts[1]);
      const seconds = parseFloat(parts[2]);
      const direction = parts[3];

      let decimal = degrees + (minutes / 60) + (seconds / 3600);
      if (direction === 'S' || direction === 'W') {
        decimal = -decimal;
      }
      return decimal;
    }

    function normalizeRadius(radiusMeters, maxDepth = 250, cubeSize = 500) {

      return ((maxDepth / 2 - radiusMeters * 0.042) / 250) * (cubeSize / 2);
    }

    // Convert coordinates to cube
    function convertCoordsToCube(lat, lng, depth, maxDepth = 500) {
      lat = dmsToDecimal(lat);
      lng = dmsToDecimal(lng);
      //console.log({ lat, lng });
      //depth = depth * .042;
      depth = cubeSize / 2 - depth * 0.042;  // Adjust here for the depth representation inside the cube
      const x = (lng / 180) * (cubeSize / 2);
      const y = (lat / 90) * (cubeSize / 2);
      let z = (depth / maxDepth) * (cubeSize / 2);  // Assuming max depth of 500 for normalization
      const normalizedZ = (depth / maxDepth) * (cubeSize / 2);  // Assuming max depth of 500 for normalization

      // Adjust the Z coordinate if necessary, depending on how you want to represent depth
      // - normalizedZ = center of cube, + normalizedZ = surface of cube
      z = cubeSize - (cubeSize / 2 - normalizedZ);

      // console.log('convertCoordsToCube', { lat, lng, depth, x, y, normalizedZ, z });
      return { x, y, z };
    }

    function convertCoordsToCubeTRY(lat, lng, depth, maxDepth = 500) {
      lat = dmsToDecimal(lat); // Convert DMS to decimal if necessary
      lng = dmsToDecimal(lng);

      // Adjust the depth
      depth = cubeSize / 2 - depth * 0.042;

      // Convert lat, lng to x, y, and constrain within cube
      let x = (lng / 180) * (cubeSize / 2);
      let y = (lat / 90) * (cubeSize / 2);

      // Ensure x and y are within the bounds of the cube
      x = Math.max(-cubeSize / 2, Math.min(x, cubeSize / 2));
      y = Math.max(-cubeSize / 2, Math.min(y, cubeSize / 2));

      // Adjust and constrain the z coordinate
      let z = (depth / maxDepth) * (cubeSize / 2);
      z = cubeSize - (cubeSize / 2 - z);
      z = Math.max(0, Math.min(z, cubeSize)); // Assuming z=0 is one face of the cube and z=cubeSize is the opposite face

      return { x, y, z };
    }

    function convertCoordsToCubeTRY2(lat, lng, depth, maxDepth = 500, cubeSize = 500) {
      lat = dmsToDecimal(lat); // Assuming this function converts DMS to decimal
      lng = dmsToDecimal(lng);

      // Assuming the cubeSize represents the total edge length of the cube
      // and the cube is centered at the origin, adjust x and y accordingly:
      let x = ((lng + 180) / 360) * cubeSize - cubeSize / 2; // Map lng to cube width
      let z = ((lat + 90) / 180) * cubeSize - cubeSize / 2; // Map lat to cube height

      // Map depth to z, where depth=0 corresponds to z=cubeSize/2 (top surface)
      let y = cubeSize / 2 - (depth / maxDepth) * cubeSize; // This assumes depth increases downwards

      // Ensure coordinates are within cube boundaries
      x = Math.max(-cubeSize / 2, Math.min(x, cubeSize / 2));
      z = Math.max(-cubeSize / 2, Math.min(z, cubeSize / 2));
      y = Math.max(0, Math.min(y, cubeSize)); // Assuming you want z within [0, cubeSize] and cubeSize/2 is the top surface

      return { x, y, z };
    }

    function convertMeasurementsToCube(x, y, depth, maxDepth, convertedXY = false) {
      let maxX = 500;
      let maxY = 500
      // Normalize each coordinate to fit within the cube
      let normalizedX = (x / maxX) * (cubeSize / 2);
      let normalizedY = (y / maxY) * (cubeSize / 2);
      let normalizedZ = (depth / maxDepth) * (cubeSize / 2);
      if (convertedXY) {
        normalizedX = x;
        normalizedY = y;
      }

      // Adjust the Z coordinate if necessary, depending on how you want to represent depth
      // - normalized = center of cube, + normalized = surface of cube
      const z = cubeSize / 2 - normalizedZ;

      //console.log('convertCoordsToCube', { cubeSize, normalizedX, normalizedY, z });
      return { x: normalizedX, y: normalizedY, z: z };
    }

    function calculateStageCoordinates(surfaceCoords, bottomCoords, stages, stageLength, wellTotalDepth, maxDepth, xOffset = 0, yOffset = 0) {
      let stageCoordinates = [bottomCoords];

      // Calculate the difference between surface and bottom coordinates for each axis
      let deltaX = surfaceCoords.x - bottomCoords.x;
      let deltaY = surfaceCoords.y - bottomCoords.y;
      let deltaZ = surfaceCoords.z - bottomCoords.z;

      for (let stageEnd = 1; stageEnd <= stages; stageEnd++) {
        // Calculate the proportion of the total difference for the current stage
        /*let stageProportion = (stageLength * stage) / deltaZ;

        // Calculate the coordinates for the current stage
        let stageX = bottomCoords.x + (deltaX * stageProportion) + xOffset;
        let stageY = bottomCoords.y + (deltaY * stageProportion) + yOffset;
        let stageZ = bottomCoords.z - (stageLength * stage);

        // Add the calculated stage coordinates to the array
        stageCoordinates.push({ stage: stage, x: stageX, y: stageY, z: stageZ });*/

        let convertedCoords = convertMeasurementsToCube(surfaceCoords.x, surfaceCoords.y, wellTotalDepth - (stageLength * stageEnd), maxDepth, true);
        stageCoordinates.push({ stageEnd: stageEnd, ...convertedCoords });
      }

      stageCoordinates.push(surfaceCoords);
      return stageCoordinates;
    }

    function onPointClick(event) {
      mouse.x = (event.clientX / window.innerWidth) * 2 - 1;
      mouse.y = -(event.clientY / window.innerHeight) * 2 + 1;

      // Check for intersections
      raycaster.setFromCamera(mouse, camera);
      const intersects = raycaster.intersectObjects(scene.children);

      if (intersects.length > 0) {
        const firstIntersection = intersects[0];
        const clickedObject = firstIntersection.object;

        // Handle the click event for the clicked object
        handlePointClick(clickedObject);
      }
    }

    function handlePointClick(clickedObject) {
      // Your logic here. For example:
      //console.log("Clicked on point:", clickedObject);
    }

    let data = [];

    function createCylinderBetweenPoints(point1, point2, material, radius = 2) {

      const direction = new THREE.Vector3().subVectors(point2, point1);
      const length = direction.length();

      // Create a cylinder that spans the distance between the two points
      const edgeGeometry = new THREE.CylinderGeometry(
        radius, // radius top
        radius, // radius bottom
        length,  // height of the cylinder
        8, // radial segments
        1 // height segments
      );

      const edge = new THREE.Mesh(edgeGeometry, material);

      // Position the cylinder
      edge.position.x = (point1.x + point2.x) / 2;
      edge.position.y = (point1.y + point2.y) / 2;
      edge.position.z = (point1.z + point2.z) / 2;

      // Use the quaternion to orient the cylinder to align with the direction vector
      edge.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), direction.normalize());

      return edge;

    }

    function addRoundedCapsToCylinder(scene, point1, point2, radius, material) {
      const sphereGeometry = new THREE.SphereGeometry(radius, 16, 16);

      const startSphere = new THREE.Mesh(sphereGeometry, material);
      startSphere.position.set(point1.x, point1.y, point1.z);
      scene.add(startSphere);

      const endSphere = new THREE.Mesh(sphereGeometry, material);
      endSphere.position.set(point2.x, point2.y, point2.z);
      scene.add(endSphere);
    }

    function createAzimuthCircle(center, cylinderDirection, radius, thickness, angleInDegrees = 45) {
      const geometry = new THREE.CircleGeometry(radius - thickness, radius, 32);
      const material = new THREE.MeshBasicMaterial({ color: 0x800080, side: THREE.DoubleSide, transparent: true, opacity: 0.5 });  // Yellow color, can be adjusted
      const circle = new THREE.Mesh(geometry, material);
      // Convert angle from degrees to radians
      const angleInRadians = THREE.MathUtils.degToRad(angleInDegrees);

      // Position at the center of the cylinder
      circle.position.set(center.x, center.y, center.z);

      // Rotate to be perpendicular to the cylinder's direction
      const up = new THREE.Vector3(0, 1, 0);
      const rotationAxis = up.clone().cross(cylinderDirection).normalize();
      circle.quaternion.setFromAxisAngle(rotationAxis, Math.acos(cylinderDirection.dot(up)));
      circle.rotateOnAxis(cylinderDirection, angleInRadians);
      return circle;
    }

    function createAzimuthRing(center, cylinderDirection, radius, thickness, angleInDegrees = 45) {
      const geometry = new THREE.RingGeometry(radius - thickness, radius, 32);
      const material = new THREE.MeshBasicMaterial({ color: 0x800080, side: THREE.DoubleSide, transparent: true, opacity: 0.5 });  // Yellow color, can be adjusted
      const circle = new THREE.Mesh(geometry, material);
      // Convert angle from degrees to radians
      const angleInRadians = THREE.MathUtils.degToRad(angleInDegrees);

      // Position at the center of the cylinder
      circle.position.set(center.x, center.y, center.z);

      // Rotate to be perpendicular to the cylinder's direction
      const up = new THREE.Vector3(0, 1, 0);
      const rotationAxis = up.clone().cross(cylinderDirection).normalize();
      circle.quaternion.setFromAxisAngle(rotationAxis, Math.acos(cylinderDirection.dot(up)));
      circle.rotateOnAxis(cylinderDirection, angleInRadians);
      return circle;
    }

    // IN USE
    function createStackedRing(center, cylinderDirection, outerRadius, innerRadius, thickness, angleInDegrees = 45) {
      // Create the ring shape
      const shape = new THREE.Shape().absarc(0, 0, outerRadius, 0, Math.PI * 2, false);
      const holePath = new THREE.Path().absarc(0, 0, innerRadius, 0, Math.PI * 2, true);
      shape.holes.push(holePath);

      // Extrude the shape to give it thickness
      const geometry = new THREE.ExtrudeGeometry(shape, {
        depth: thickness,
        bevelEnabled: false,
        //steps: 2
      });

      const material = new THREE.MeshBasicMaterial({ color: 0xffff00, transparent: true, opacity: 0.5 });  // Purple color
      const circle = new THREE.Mesh(geometry, material);

      // Convert angle from degrees to radians
      const angleInRadians = THREE.MathUtils.degToRad(angleInDegrees);

      // Position at the center of the cylinder
      circle.position.set(center.x, center.y - thickness / 2, center.z);  // Subtract half the thickness to position correctly

      // Rotate to be perpendicular to the cylinder's direction
      const up = new THREE.Vector3(0, 1, 0);
      const rotationAxis = up.clone().cross(cylinderDirection).normalize();
      circle.quaternion.setFromAxisAngle(rotationAxis, Math.acos(cylinderDirection.dot(up)));
      //circle.rotateOnAxis(cylinderDirection, angleInRadians);
      //rotate on earth y axis
      //circle.rotateOnAxis(up, angleInRadians);
      circle.rotateX(angleInRadians);

      return circle;
    }

    function createSphere(center, radius = 5, color = 0xff0000) {
      // hollow interior
      const geometry = new THREE.SphereGeometry(radius, 32, 32);
      const material = new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.6 });
      const sphere = new THREE.Mesh(geometry, material);
      sphere.position.set(center.x, center.y, center.z);
      return sphere;

      // double-sided sphere (filled interior)
      /* const geometry = new THREE.SphereGeometry(radius, 32, 32);
      const material = new THREE.MeshBasicMaterial({
        color,
        transparent: true,
        opacity: 0.6,
        side: THREE.DoubleSide, // 👈 key part
      });
      const sphere = new THREE.Mesh(geometry, material);
      sphere.position.set(center.x, center.y, center.z);
      return sphere;*/
    }

    function createOriginMarker(center, size = 15, color = 0x000000) {
      const geometry = new THREE.BufferGeometry().setFromPoints([new THREE.Vector3(center.x, center.y, center.z)]);
      const material = new THREE.PointsMaterial({ color, size, sizeAttenuation: false });
      return new THREE.Points(geometry, material);
    }

    //create cylinders
    const cylinderMaterial = new THREE.MeshBasicMaterial({
      color: "#707070"
    });
    const cylinderMaterial1 = new THREE.MeshBasicMaterial({
      color: "#FF0000"
    }); // Red color
    const cylinderMaterial2 = new THREE.MeshBasicMaterial({
      color: "#0000FF"
    }); // Blue color

    // fpz outer cylinder
    let outerMaterial = new THREE.MeshBasicMaterial({
      color: 0x00ffff,
      transparent: true,
      opacity: 0.2,
      side: THREE.DoubleSide
    });

    let minPosX = Infinity;
    let maxPosX = -Infinity;
    let minPosY = Infinity;
    let maxPosY = -Infinity;
    let minPosZ = Infinity;
    let maxPosZ = -Infinity;

    // HANDLE PLOTTING
    // get maxDepth
    let maxDepth = 250;
    directionalSurveys.forEach(obj => {
      obj.coordinates.forEach(coord => {
        maxDepth = Math.max(maxDepth, coord.depth);
      });
    });
    console.log('maxDepth', maxDepth);
    // Loop through all coordinates to find min and max positions
    directionalSurveys.forEach(obj => {
      obj.coordinates.forEach(coord => {
        // console.log('coord', coord);
        const point = convertCoordsToCubeTRY2(dmsToDecimal(coord.lat), dmsToDecimal(coord.lng), coord.depth, maxDepth);
        minPosX = Math.min(minPosX, point.x);
        maxPosX = Math.max(maxPosX, point.x);
        minPosY = Math.min(minPosY, point.y);
        maxPosY = Math.max(maxPosY, point.y);
        minPosZ = Math.min(minPosZ, point.z);
        maxPosZ = Math.max(maxPosZ, point.z);
      });
    });

    // Calculate the offset ranges
    const offsetXRange = Math.max(0, ((containerCube.geometry.parameters.width / 2) - (maxPosX - minPosX))); // Calculate the range of the offset for the x axis
    const offsetYRange = Math.max(0, ((containerCube.geometry.parameters.height / 2) - (maxPosY - minPosY))); // Calculate the range of the offset for the y axis
    const offsetZRange = Math.max(0, ((containerCube.geometry.parameters.depth / 2) - (maxPosZ - minPosZ))); // Calculate the range of the offset for the z axis

    for (let [index, obj] of directionalSurveys.entries()) {
      let isSelected = false;
      if (selectedTreatmentWell) {
        isSelected = (obj?.coordinates[0]?.well?.unique_identifier === selectedTreatmentWell?.unique_identifier);
      } else {
        isSelected = selectedTreatmentWells?.some(
          well => well?.unique_identifier === obj?.coordinates[0]?.well?.unique_identifier
        );
      }

      if (!toggleOffsetWells && !isSelected) {
        continue; // Skip this object if not selected and offset wells are toggled off
      }

      const { heel, toe } = obj;
      let outerStart, outerEnd;
      // get well data 
      let thisWell;
      if (groupedStages) {
        for (let toolId of Object.keys(groupedStages)) {
          if (groupedStages[toolId].stages && groupedStages[toolId].stages[0].well.wellName.toLowerCase() === obj.coordinates[0].wellName.toLowerCase()) {
            // Found a match, now you can use toolId
            thisWell = groupedStages[toolId];
            break;
          }
        }
      }
      //console.log('thisWell', thisWell);

      for (let i = 0; i < obj.coordinates.length - 1; i++) {
        console.log('length', obj.coordinates.length, 'i', i, 'coordinates', obj.coordinates[i], obj.coordinates[i + 1]);
        let start = convertCoordsToCubeTRY2(dmsToDecimal(obj.coordinates[i].lat), dmsToDecimal(obj.coordinates[i].lng), obj.coordinates[i].depth, maxDepth);
        let end = convertCoordsToCubeTRY2(dmsToDecimal(obj.coordinates[i + 1].lat), dmsToDecimal(obj.coordinates[i + 1].lng), obj.coordinates[i + 1].depth, maxDepth);

        // Apply offsets
        start.x += (start.x - minPosX) / (maxPosX - minPosX) * (offsetXRange - 100);
        start.y += (start.y - minPosY) / (maxPosY - minPosY) * (offsetYRange);
        start.z += (start.z - minPosZ) / (maxPosZ - minPosZ) * (offsetZRange - 100);

        end.x += (end.x - minPosX) / (maxPosX - minPosX) * (offsetXRange - 100);
        end.y += (end.y - minPosY) / (maxPosY - minPosY) * (offsetYRange);
        end.z += (end.z - minPosZ) / (maxPosZ - minPosZ) * (offsetZRange - 100);

        //let chosenMaterial = (i % 2 === 0) ? cylinderMaterial1 : cylinderMaterial2;
        //if (i === 0 || !obj.coordinates[i]?.stage) chosenMaterial = cylinderMaterial;
        //if (obj.coordinates[0]?.stage?.toLowerCase()?.includes('offset')) {
        //chosenMaterial = cylinderMaterial;
        //}
        // console.log('chosenMaterial', obj.coordinates[0]?.well.unique_identifier, selectedTreatmentWell.unique_identifier);
        let chosenMaterial = (isSelected) ? cylinderMaterial1 : cylinderMaterial;

        if (obj.coordinates[i].lat === heel?.lat && obj.coordinates[i].lng === heel?.lng) {
          outerStart = start;
        }
        if (obj.coordinates[i + 1].lat === toe?.lat && obj.coordinates[i + 1].lng === toe?.lng) {
          outerEnd = end;
        }


        const cylinder = createCylinderBetweenPoints(start, end, chosenMaterial);
        cylinder.name = `cylinder-${thisWell?.stages[0].toolId}-${i}`;
        cylinderRef.current = cylinder;
        scene.add(cylinderRef.current);
        cylinderMeshes.push(cylinderRef.current);

        // text for stage
        if (font) {  // Ensure the font is loaded
          let displayText;
          if (i === 0) {
            displayText = ` ${obj.coordinates[i].well.unique_identifier}`;
          } else {
            displayText = (!obj.coordinates[0]?.stage?.toLowerCase()?.includes('offset') && obj.coordinates[i].stage) ?
              ` ${obj.coordinates[i].stage}`
              : '';
          }
          // if the first in array then display stage which will be the name of the well


          const textGeometry = new THREE.TextGeometry(
            displayText,
            {
              font: font,
              size: 4,  // font size
              height: 0,  // This makes the text flat (2D: 0, 3D: 1)
            }
          );
          const textMaterial = new THREE.MeshBasicMaterial({ color: 0x000000 });
          const textMesh = new THREE.Mesh(textGeometry, textMaterial);

          // Position the text at the midpoint of the cylinder
          textMesh.position.x = (start.x + end.x) / 2 + 1;
          textMesh.position.y = (start.y + end.y) / 2;
          textMesh.position.z = (start.z + end.z) / 2;
          textMeshRef.current = textMesh;
          scene.add(textMeshRef.current);
          textMeshes.push(textMesh);
        }


        console.log('subjectWell', obj);
        console.log('thisWell', thisWell);

        // Get cylinder centroid
        const cylinderCenter = new THREE.Vector3(
          (start.x + end.x) / 2,
          (start.y + end.y) / 2,
          (start.z + end.z) / 2
        );

        // outer cylinder if stage is not offset
        /*if (isSelected) {
          console.log('start', start, 'end', end);
          const cylinder2 = createCylinderBetweenPoints(start, end, outerMaterial, 50);
          cylinder2.name = `cylinder2-${thisWell?.stages[0].toolId}-${i}`;
          cylinderRef.current = cylinder2;
          scene.add(cylinderRef.current);
          cylinderMeshes.push(cylinderRef.current);
        }*/

        const cylinderDirection = new THREE.Vector3().subVectors(end, start).normalize();
        //if (index === 1 && i === 0) {
        //const azimuthCircle = createAzimuthCircle(cylinderCenter, cylinderDirection, cubeSize / 2, 1, 75);
        //scene.add(azimuthCircle);
        //}

        //if (index === 1 && i === 0) {
        //const azimuthRing = createStackedRing(cylinderCenter, cylinderDirection, cubeSize / 2, 0, 2, 45);   
        //scene.add(azimuthRing);
        //}

        if (isSelected) {
          const sphere = createSphere(cylinderCenter, 500);
          const originMarker = createOriginMarker(cylinderCenter);

          //scene.add(sphere);
          //scene.add(originMarker);

        }

      }

      // outer cylinder if stage is not offset
      if (isSelected && heel && toe && toggleFpzRadius) {
        console.log('***start', outerStart, 'end', outerEnd);
        // get color of largest fpz radius
        let fpzColor = selectedTreatmentWells
          ?.find(well => well?.unique_identifier === obj?.unique_identifier)
          ?.fpzRadiuses?.at(-1)?.color || '00ffff';
        let colorValue = parseInt(fpzColor.replace("#", ""), 16);
        console.log({ fpzColor, colorValue });
        outerMaterial = new THREE.MeshBasicMaterial({
          color: colorValue,
          transparent: true,
          opacity: 0.2,
          side: THREE.DoubleSide
        });

        const cylinder2 = createCylinderBetweenPoints(outerStart, outerEnd, outerMaterial, normalizeRadius(500));
        cylinder2.name = `fpz-${index}`;
        cylinderRef.current = cylinder2;
        scene.add(cylinderRef.current);
        cylinderMeshes.push(cylinderRef.current);
        addRoundedCapsToCylinder(scene, outerStart, outerEnd, normalizeRadius(500), outerMaterial);
      }
    }


    function onCanvasClick(event) {
      // Convert mouse coordinates to normalized device coordinates
      mouse.x = (event.clientX / window.innerWidth) * 2 - 1;
      mouse.y = -(event.clientY / window.innerHeight) * 2 + 1;

      // Update the ray with the camera and mouse position
      raycaster.setFromCamera(mouse, camera);

      // Calculate objects intersecting the picking ray
      //const intersects = raycaster.intersectObjects({ ...cylinderMeshes, ...textMeshes });
      const intersects = raycaster.intersectObjects(cylinderMeshes);
      console.log('onCanvasClick', intersects);

      if (intersects.length > 0) {
        const clickedCylinder = intersects[0].object;
        console.log('You clicked on:', clickedCylinder.name);
        let wellInfo = clickedCylinder.name.split('-');
        let selectedStage = groupedStages[wellInfo[1]]?.stages[wellInfo[2]];
        handleStageClick(selectedStage);
      }
    }

    // Add an event listener for mouse clicks
    // canvasRef.current.addEventListener('click', onCanvasClick);

    // x = 145.61163014958, y = 116.79541758764991, z = 664.8030731581399
    camera.position.x = -16.27;
    camera.position.y = 212.60;
    camera.position.z = 526.2;
    //camera.fov = 45; // A smaller FOV to make objects appear larger

    let hasCaptured = false;
    const animate = () => {
      requestAnimationFrame(animate);
      controls.update();
      renderer.render(scene, camera);
      // console.log(`Camera Position: x = ${camera.position.x}, y = ${camera.position.y}, z = ${camera.position.z}`);

      if (!hasCaptured) {
        requestAnimationFrame(() => {
          const url = renderer.domElement.toDataURL("image/png");
          setCubeImageDataUrl(url);
          hasCaptured = true;
        });
      }
    };

    animate();

  }, []);

  /*
  useEffect(() => {
    
    if (selectedStageData?.azimuth && selectedStageData.azimuth > 0){
      //const selectedAzimuth = scene.getObjectByName(`ring-${selectedStageData.toolId}-${selectedStageData.stage}`);
    }
    
  }, [selectedStageData]);
  */


  return (
    <div ref={containerRef} {...rest} pt={2} px={2} >
      <canvas ref={canvasRef} width="90%" height="500" style={{ outline: "none", ...canvasStyle }} />
    </div>
  );
});

Cube.defaultProps = {
  canvasStyle: {},
};

Cube.propTypes = {
  canvasStyle: PropTypes.objectOf(PropTypes.any),
};

export default Cube;
